import React, { useState } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  useLocation,
  matchPath,
} from "react-router-dom";
import { Provider } from "react-redux";
import { LDProvider } from "launchdarkly-react-client-sdk";
import { ThemeProvider } from "@emotion/react";
import store from "../../store";
import theme from "../../themes/theme";
import AuthorizationProvider from "../../common/providers/AuthorizationProvider";
import LaunchDarklyProvider from "../../common/launchDarkly/LaunchDarklyProvider";
import Sidebar, {
  SIDEBAR_EXPANDED_WIDTH,
  SIDEBAR_COLLAPSED_WIDTH,
} from "../ProjectLayout/Sidebar";
import SettingsNavigationProvider, {
  SETTINGS_NAV_HEIGHT,
} from "./SettingsNavigationProvider";

// Admin MSPAs — same components App.js routes to. Keep this import block and
// the <Routes> below in sync with App.js's /admin/* routes.
import ConnectedApps from "../../pages/Admin/ConnectedApps";
import WebhookSubscriptionsMspa from "../../pages/Admin/WebhookSubscriptions/WebhookSubscriptionsMspa";
import PromptOverridesMspa from "../../pages/Admin/PromptOverrides/PromptOverridesMspa";
import OpenRouterKeysMspa from "../../pages/Admin/OpenRouterKeys/OpenRouterKeysMspa";
import AccountInfo from "../../pages/Account/EditAccount/Advanced/AccountInfo";
import AccountPreferences from "../../pages/Account/EditAccount/Advanced/AccountPreferences";
import { AccountCreditsMspa } from "../../pages/Admin/AccountCredits";
import LinesOfBusinessMspa from "../../pages/Admin/LinesOfBusiness/LinesOfBusinessMspa";
import LanguageFieldsMspa from "../../pages/Admin/LanguageFields/LanguageFieldsMspa";
import ServiceConditionsMspa from "../../pages/Admin/ServiceConditions/ServiceConditionsMspa";
import { ProfessionalServicesMspa } from "../../pages/Admin/ProfessionalServices";
import { BlueprintsV2Mspa } from "../../pages/Admin/BlueprintsV2";
import ProductsV2Mspa from "../../pages/Admin/ProductsV2/ProductsV2Mspa";
import { DocumentTemplatesMspa } from "../../pages/Admin/DocumentTemplates";
import TextBlocksMspa from "../../pages/Admin/TextBlocks/TextBlocksMspa";
import AttachmentTypesMspa from "../../pages/Admin/AttachmentTypes/AttachmentTypesMspa";
import QuestionnairesMspa from "../../pages/Admin/Questionnaires/QuestionnairesMspa";
import CurrenciesMspa from "../../pages/Admin/Currencies/CurrenciesMspa";
import ResourcesMspa from "../../pages/Admin/Resources/ResourcesMspa";
import RateTablesMspa from "../../pages/Admin/RateTables/RateTablesMspa";
import ExpenseCategoriesMspa from "../../pages/Admin/ExpenseCategories/ExpenseCategoriesMspa";
import PaymentTermsMspa from "../../pages/Admin/PaymentTerms/PaymentTermsMspa";
import PaymentCreditsMspa from "../../pages/Admin/PaymentCredits/PaymentCreditsMspa";
import GovernancesMspa from "../../pages/GovernanceCatalogV2/GovernancesMspa";
import PhasesMspa from "../../pages/GovernanceCatalogV2/PhasesMspa";
import UserDefinedFieldsMspa from "../../pages/GovernanceCatalogV2/UserDefinedFieldsMspa";
import ProjectApprovalV1Mspa from "../../pages/Admin/ProjectApprovalV1Mspa";
import ProjectApprovalV2Mspa from "../../pages/Admin/ProjectApprovalV2Mspa/ProjectApprovalV2Mspa";
import UsersMspa from "../../pages/Account/EditAccount/Users";
import SalesExecutivesMspa from "../../pages/Admin/SalesExecutives/SalesExecutivesMspa";
import RolesMspa from "../../pages/Admin/Roles/RolesMspa";
import TeamsMspa from "../../pages/Admin/Teams/TeamsMspa";
import BusinessUnitsMspa from "../../pages/Admin/BusinessUnits/BusinessUnitsMspa";
import ClientsMspa from "../../pages/Admin/Clients/ClientsMspa";
import VendorsV2Mspa from "../../pages/Admin/VendorsV2/VendorsV2Mspa";
import { ReleaseFlagsMspa } from "../../pages/Admin/ReleaseFlags";
import useFlags from "../../common/hooks/useFlags";
import AccountMspa from "../../pages/Account/AccountMspa";

interface SettingsAppLayoutProps {
  account_slug: string;
  account_id: string;
}

// Mirrors App.js's FlaggedProjectApprovalMspa: unified ApprovalLevel UI when
// release.unified-approval-workflow is on, else the legacy LobApproval UI.
// ProjectApprovalV1Mspa/V2Mspa take no props (they read account context via
// useAuthorization(), like the rest of the admin MSPAs below).
const FlaggedProjectApprovalMspa: React.FC<SettingsAppLayoutProps> = () => {
  const { releaseUnifiedApprovalWorkflow } = useFlags();
  return releaseUnifiedApprovalWorkflow ? (
    <ProjectApprovalV2Mspa />
  ) : (
    <ProjectApprovalV1Mspa />
  );
};

export const SettingsAppLayoutInner: React.FC<SettingsAppLayoutProps> = ({
  account_slug,
  account_id,
}) => {
  const [sidebarExpanded, setSidebarExpanded] = useState(true);
  const sidebarWidth = sidebarExpanded
    ? SIDEBAR_EXPANDED_WIDTH
    : SIDEBAR_COLLAPSED_WIDTH;

  const location = useLocation();
  // The user-profile page renders sidebar-only (no settings top-nav) so it
  // looks identical whether it's reached here or from the project chrome.
  const onProfile = !!matchPath("/users/:user_id/edit/*", location.pathname);

  const props = { account_slug, account_id };

  return (
    <>
      {/* reloadDocument: the sidebar's top-level nav items (Projects, Home,
          …) are cross-area destinations with no route in this settings root,
          so they must full-page GET rather than client-side navigate to a
          blank content area. clientSideNav only governs the User Profile item
          (UserMenuCard), which IS routed here (/users/*). */}
      <Sidebar
        expanded={sidebarExpanded}
        onToggle={() => setSidebarExpanded((prev) => !prev)}
        clientSideNav
        reloadDocument
      />
      {!onProfile && (
        <SettingsNavigationProvider sidebarWidth={sidebarWidth} clientSideNav />
      )}
      <div
        style={{
          marginLeft: sidebarWidth,
          paddingTop: onProfile ? 0 : SETTINGS_NAV_HEIGHT,
          minHeight: "100vh",
          backgroundColor: "#F8F9FA",
          transition: "margin-left 0.2s ease",
        }}
      >
        <div className="container-fluid">
          <Routes>
            <Route path="/users/*" element={<AccountMspa />} />
            {/* AccountInfo, AccountPreferences, and every admin MSPA below
                (aside from ConnectedApps and ProfessionalServicesMspa) take
                no props verbatim from App.js — they read account context via
                useAuthorization()/context, which AuthorizationProvider above
                already supplies. Passing {...props} to them is a TS2559 type
                error in a .tsx file (App.js gets away with it only because
                it's untyped JS), so they're rendered bare here to match their
                real, verified signatures. */}
            <Route path="/admin/account_info" element={<AccountInfo />} />
            <Route
              path="/admin/account_preferences"
              element={<AccountPreferences />}
            />
            <Route
              path="/admin/account_credits/*"
              element={<AccountCreditsMspa />}
            />
            <Route
              path="/admin/account_connections/*"
              element={<ConnectedApps {...props} />}
            />
            <Route
              path="/admin/webhook_subscriptions/*"
              element={<WebhookSubscriptionsMspa />}
            />
            <Route
              path="/admin/prompt_overrides/*"
              element={<PromptOverridesMspa />}
            />
            <Route
              path="/admin/open_router_keys/*"
              element={<OpenRouterKeysMspa />}
            />
            <Route path="/admin/sections/*" element={<LinesOfBusinessMspa />} />
            <Route
              path="/admin/language_fields/*"
              element={<LanguageFieldsMspa />}
            />
            <Route
              path="/admin/service_conditions/*"
              element={<ServiceConditionsMspa />}
            />
            <Route
              path="/admin/tasks/*"
              element={
                <ProfessionalServicesMspa
                  {...props}
                  serviceType="professional_services"
                />
              }
            />
            <Route
              path="/admin/managed_services/*"
              element={
                <ProfessionalServicesMspa
                  {...props}
                  serviceType="managed_services"
                />
              }
            />
            <Route path="/admin/blueprints/*" element={<BlueprintsV2Mspa />} />
            <Route
              path="/admin/materials/*"
              element={<ProductsV2Mspa productType="products" />}
            />
            <Route
              path="/admin/document_templates/*"
              element={<DocumentTemplatesMspa />}
            />
            <Route path="/admin/text_blocks/*" element={<TextBlocksMspa />} />
            <Route
              path="/admin/attachment_types/*"
              element={<AttachmentTypesMspa />}
            />
            <Route
              path="/admin/questionnaires/*"
              element={<QuestionnairesMspa />}
            />
            <Route path="/admin/currencies/*" element={<CurrenciesMspa />} />
            <Route path="/admin/resources/*" element={<ResourcesMspa />} />
            <Route path="/admin/rate_tables/*" element={<RateTablesMspa />} />
            <Route
              path="/admin/expense_categories/*"
              element={<ExpenseCategoriesMspa />}
            />
            <Route
              path="/admin/payment_terms/*"
              element={<PaymentTermsMspa />}
            />
            <Route
              path="/admin/payment_credits/*"
              element={<PaymentCreditsMspa />}
            />
            <Route path="/admin/governances/*" element={<GovernancesMspa />} />
            <Route path="/admin/phases/*" element={<PhasesMspa />} />
            <Route
              path="/admin/project_variables/*"
              element={<UserDefinedFieldsMspa />}
            />
            <Route
              path="/admin/approval_levels/*"
              element={<FlaggedProjectApprovalMspa {...props} />}
            />
            <Route
              path="/admin/lob_approvals/*"
              element={<FlaggedProjectApprovalMspa {...props} />}
            />
            <Route path="/admin/users/*" element={<UsersMspa />} />
            <Route
              path="/admin/sales_executives/*"
              element={<SalesExecutivesMspa />}
            />
            <Route path="/admin/roles/*" element={<RolesMspa />} />
            <Route path="/admin/teams/*" element={<TeamsMspa />} />
            <Route
              path="/admin/business_units/*"
              element={<BusinessUnitsMspa />}
            />
            <Route path="/admin/clients/*" element={<ClientsMspa />} />
            <Route path="/admin/vendors/*" element={<VendorsV2Mspa />} />
            <Route
              path="/admin/release_flags/*"
              element={<ReleaseFlagsMspa />}
            />
          </Routes>
        </div>
      </div>
    </>
  );
};

const SettingsAppLayout: React.FC<SettingsAppLayoutProps> = (props) => {
  const { account_slug } = props;
  return (
    <Provider store={store}>
      <AuthorizationProvider account_slug={account_slug}>
        <ThemeProvider theme={theme}>
          <LDProvider
            clientSideID={
              process.env.REACT_APP_LAUNCHDARKLY_CLIENT_ID || "dummyclient"
            }
          >
            <LaunchDarklyProvider accountSlug={account_slug}>
              <BrowserRouter>
                <SettingsAppLayoutInner {...props} />
              </BrowserRouter>
            </LaunchDarklyProvider>
          </LDProvider>
        </ThemeProvider>
      </AuthorizationProvider>
    </Provider>
  );
};

export default SettingsAppLayout;
